import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ArrowRightIcon, ArrowLeftIcon, CheckIcon, DollarSignIcon, ClockIcon, UserIcon, PhoneIcon, MailIcon, HomeIcon, MapPinIcon, CalendarIcon, FileTextIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
export const FundingForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    amountNeeded: '',
    dealType: '',
    exitStrategy: '',
    experienceLevel: '',
    inspectionEndDate: '',
    dealExplanation: '',
    closingDate: '',
    termsAgreed: false,
    purchaseContractUrl: '',
    abContractUrl: '',
    bcContractUrl: ''
  });
  const [inspectionDate, setInspectionDate] = useState<Date | null>(null);
  const [closingDateValue, setClosingDateValue] = useState<Date | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const totalSteps = 5;
  const AIRTABLE_API_KEY = 'patHVFB5C314V5TXg.c0ab941dba7a1061c26123ca8f08d0b276b43f4943da62bd277a1cd3dada5396';
  const AIRTABLE_BASE_ID = 'applACJWsk05IKTO1';
  const AIRTABLE_TABLE_ID = 'tblcECBr7GcjDdy8z';
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: val
    }));
  };
  const handleInspectionDateChange = (date: Date | null) => {
    setInspectionDate(date);
    if (date) {
      const formattedDate = date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      setFormData(prev => ({
        ...prev,
        inspectionEndDate: formattedDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        inspectionEndDate: ''
      }));
    }
  };
  const handleClosingDateChange = (date: Date | null) => {
    setClosingDateValue(date);
    if (date) {
      const formattedDate = date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      setFormData(prev => ({
        ...prev,
        closingDate: formattedDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        closingDate: ''
      }));
    }
  };
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const validateForm = () => {
    const errors: {
      [key: string]: string;
    } = {};
    if (!formData.firstName || !formData.firstName.trim()) {
      errors.firstName = 'First name is required.';
    }
    if (!formData.lastName || !formData.lastName.trim()) {
      errors.lastName = 'Last name is required.';
    }
    if (!formData.email || !formData.email.trim()) {
      errors.email = 'Email is required.';
    }
    if (!formData.phone || !formData.phone.trim()) {
      errors.phone = 'Phone number is required.';
    }
    if (!formData.address || !formData.address.trim()) {
      errors.address = 'Address is required.';
    }
    if (!formData.city || !formData.city.trim()) {
      errors.city = 'City is required.';
    }
    if (!formData.state || !formData.state.trim()) {
      errors.state = 'State is required.';
    }
    if (!formData.amountNeeded || !formData.amountNeeded.trim()) {
      errors.amountNeeded = 'Amount needed is required.';
    }
    if (!formData.dealType) {
      errors.dealType = 'Please select a deal type.';
    }
    if (!formData.exitStrategy) {
      errors.exitStrategy = 'Please select an exit strategy.';
    }
    if (!formData.experienceLevel) {
      errors.experienceLevel = 'Please select your experience level.';
    }
    if (!formData.inspectionEndDate) {
      errors.inspectionEndDate = 'Inspection end date is required.';
    }
    if (!formData.closingDate) {
      errors.closingDate = 'Closing date is required.';
    }
    if (formData.purchaseContractUrl && formData.purchaseContractUrl.trim() !== '') {
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(formData.purchaseContractUrl)) {
        errors.purchaseContractUrl = 'Please enter a valid URL (must start with http:// or https://)';
      }
    }
    if (formData.abContractUrl && formData.abContractUrl.trim() !== '') {
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(formData.abContractUrl)) {
        errors.abContractUrl = 'Please enter a valid URL (must start with http:// or https://)';
      }
    }
    if (formData.bcContractUrl && formData.bcContractUrl.trim() !== '') {
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(formData.bcContractUrl)) {
        errors.bcContractUrl = 'Please enter a valid URL (must start with http:// or https://)';
      }
    }
    if (!formData.termsAgreed) {
      errors.termsAgreed = 'Please agree to the terms before submitting.';
    }
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSubmissionError(Object.values(errors)[0]);
      return false;
    }
    return true;
  };
  const mapDealType = value => {
    const mapping = {
      earnestMoney: 'Earnest Money',
      emd: 'Earnest Money',
      doubleClose: 'Double Closing',
      doubleClosing: 'Double Closing',
      stackMethod: 'Stack Method',
      stack: 'Stack Method'
    };
    return mapping[value] || 'Earnest Money';
  };
  const mapExitStrategy = value => {
    const mapping = {
      wholesale: 'Wholesale',
      assignment: 'Wholesale',
      fixAndFlip: 'Fix & Flip',
      fixFlip: 'Fix & Flip',
      fix: 'Fix & Flip',
      buyAndHold: 'Buy & Hold',
      buyHold: 'Buy & Hold',
      rental: 'Buy & Hold',
      other: 'Other'
    };
    return mapping[value] || 'Wholesale';
  };
  const mapExperienceLevel = value => {
    const mapping = {
      beginner: 'Beginner',
      intermediate: 'Intermediate',
      advanced: 'Experienced',
      experienced: 'Experienced',
      expert: 'Experienced'
    };
    return mapping[value] || 'Beginner';
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      if (!validateForm()) {
        setIsSubmitting(false);
        return;
      }
      const ZAPIER_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/12781423/4y9uhzj/';
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        fullName: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        amountNeeded: parseFloat(formData.amountNeeded.replace(/[^0-9.]/g, '')),
        amountNeededRaw: formData.amountNeeded,
        dealType: mapDealType(formData.dealType),
        exitStrategy: mapExitStrategy(formData.exitStrategy),
        experienceLevel: mapExperienceLevel(formData.experienceLevel),
        inspectionEndDate: formData.inspectionEndDate,
        closingDate: formData.closingDate,
        dealExplanation: formData.dealExplanation,
        purchaseContractUrl: formData.purchaseContractUrl || 'Not provided',
        abContractUrl: formData.abContractUrl || 'Not provided',
        bcContractUrl: formData.bcContractUrl || 'Not provided',
        termsAgreed: formData.termsAgreed,
        submissionDate: new Date().toISOString(),
        source: 'ScaleWithLending Funding Form'
      };
      // Zapier webhooks don't return CORS headers for browser requests with
      // Content-Type: application/json (which triggers a preflight). We use
      // text/plain to keep this a "simple request" — Zapier still parses the
      // JSON body correctly.
      await fetch(ZAPIER_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain;charset=UTF-8'
        },
        body: JSON.stringify(payload)
      });
      navigate('/thank-you');
    } catch (error) {
      console.error('Form submission error:', error);
      if (error instanceof Error) {
        const errorMessage = error.message || '';
        if (errorMessage.includes('network') || errorMessage.includes('Failed to fetch')) {
          setSubmissionError('Network error: Please check your internet connection and try again later.');
        } else {
          setSubmissionError(`Error submitting form: ${errorMessage}. Please try again or contact support.`);
        }
      } else {
        setSubmissionError('There was an error submitting your form. Please try again or contact support@scalewithlending.com directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderStepIndicator = () => {
    return <div className="mb-8 relative" data-id="element-230">
        <div className="flex justify-between items-center relative z-10" data-id="element-231">
          {[1, 2, 3, 4, 5].map(step => <div key={step} className="flex flex-col items-center" data-id="element-232">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${currentStep === step ? 'bg-[#c9a875] text-white shadow-[0_0_15px_rgba(201,168,117,0.5)]' : currentStep > step ? 'bg-[#c9a875] text-white' : 'bg-[#1c1916] text-[#f2efeb]/50 border border-[#403a35]'}`} data-id="element-233">
                {currentStep > step ? <CheckIcon className="w-5 h-5" data-id="element-234" /> : step}
              </div>
              <span className={`text-xs mt-2 hidden sm:block ${currentStep >= step ? 'text-[#c9a875]' : 'text-[#f2efeb]/50'}`} data-id="element-235">
                {step === 1 && 'Personal'}
                {step === 2 && 'Property'}
                {step === 3 && 'Deal'}
                {step === 4 && 'Contracts'}
                {step === 5 && 'Review'}
              </span>
            </div>)}
        </div>
        <div className="absolute top-5 left-0 w-full h-[2px] bg-[#1c1916] -z-0" data-id="element-236">
          <div className="h-full bg-[#c9a875] transition-all duration-500 ease-out" style={{
          width: `${(currentStep - 1) / (totalSteps - 1) * 100}%`
        }} data-id="element-237" />
        </div>
      </div>;
  };
  const renderStep1 = () => <div className="space-y-5 animate-fadeIn" data-id="element-238">
      <h3 className="text-2xl font-bold text-[#f2efeb] mb-6 flex items-center gap-2" data-id="element-239">
        <UserIcon className="w-6 h-6 text-[#c9a875]" data-id="element-240" />
        Personal Information
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-241">
        <div className="space-y-2" data-id="element-242">
          <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-243">
            First Name *
          </label>
          <div className="relative" data-id="element-244">
            <UserIcon className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'firstName' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-245" />
            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} onFocus={() => setFocusedField('firstName')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.firstName ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl pl-11 pr-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors`} placeholder="John" required data-id="element-246" />
          </div>
          {validationErrors.firstName && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-247">
              {validationErrors.firstName}
            </p>}
        </div>

        <div className="space-y-2" data-id="element-248">
          <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-249">
            Last Name *
          </label>
          <div className="relative" data-id="element-250">
            <UserIcon className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'lastName' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-251" />
            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} onFocus={() => setFocusedField('lastName')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.lastName ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl pl-11 pr-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors`} placeholder="Doe" required data-id="element-252" />
          </div>
          {validationErrors.lastName && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-253">
              {validationErrors.lastName}
            </p>}
        </div>
      </div>

      <div className="space-y-2" data-id="element-254">
        <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-255">
          Email Address *
        </label>
        <div className="relative" data-id="element-256">
          <MailIcon className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'email' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-257" />
          <input type="email" name="email" value={formData.email} onChange={handleChange} onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.email ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl pl-11 pr-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors`} placeholder="john@example.com" required data-id="element-258" />
        </div>
        {validationErrors.email && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-259">
            {validationErrors.email}
          </p>}
      </div>

      <div className="space-y-2" data-id="element-260">
        <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-261">
          Phone Number *
        </label>
        <div className="relative" data-id="element-262">
          <PhoneIcon className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'phone' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-263" />
          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} onFocus={() => setFocusedField('phone')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.phone ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl pl-11 pr-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors`} placeholder="(555) 123-4567" required data-id="element-264" />
        </div>
        {validationErrors.phone && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-265">
            {validationErrors.phone}
          </p>}
      </div>
    </div>;
  const renderStep2 = () => <div className="space-y-5 animate-fadeIn" data-id="element-266">
      <h3 className="text-2xl font-bold text-[#f2efeb] mb-6 flex items-center gap-2" data-id="element-267">
        <HomeIcon className="w-6 h-6 text-[#c9a875]" data-id="element-268" />
        Property Details
      </h3>

      <div className="space-y-2" data-id="element-269">
        <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-270">
          Property Address *
        </label>
        <div className="relative" data-id="element-271">
          <MapPinIcon className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'address' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-272" />
          <input type="text" name="address" value={formData.address} onChange={handleChange} onFocus={() => setFocusedField('address')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.address ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl pl-11 pr-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors`} placeholder="123 Main St" required data-id="element-273" />
        </div>
        {validationErrors.address && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-274">
            {validationErrors.address}
          </p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-275">
        <div className="space-y-2" data-id="element-276">
          <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-277">
            City *
          </label>
          <div className="relative" data-id="element-278">
            <MapPinIcon className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'city' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-279" />
            <input type="text" name="city" value={formData.city} onChange={handleChange} onFocus={() => setFocusedField('city')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.city ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl pl-11 pr-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors`} placeholder="Austin" required data-id="element-280" />
          </div>
          {validationErrors.city && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-281">
              {validationErrors.city}
            </p>}
        </div>

        <div className="space-y-2" data-id="element-282">
          <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-283">
            State *
          </label>
          <div className="relative" data-id="element-284">
            <MapPinIcon className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'state' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-285" />
            <input type="text" name="state" value={formData.state} onChange={handleChange} onFocus={() => setFocusedField('state')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.state ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl pl-11 pr-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors`} placeholder="TX" required data-id="element-286" />
          </div>
          {validationErrors.state && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-287">
              {validationErrors.state}
            </p>}
        </div>
      </div>
    </div>;
  const renderStep3 = () => <div className="space-y-5 animate-fadeIn" data-id="element-288">
      <h3 className="text-2xl font-bold text-[#f2efeb] mb-6 flex items-center gap-2" data-id="element-289">
        <DollarSignIcon className="w-6 h-6 text-[#c9a875]" data-id="element-290" />
        Deal Information
      </h3>

      <div className="space-y-2" data-id="element-291">
        <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-292">
          Funding Amount Needed *
        </label>
        <div className="relative" data-id="element-293">
          <DollarSignIcon className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'amountNeeded' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-294" />
          <input type="text" name="amountNeeded" value={formData.amountNeeded} onChange={handleChange} onFocus={() => setFocusedField('amountNeeded')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.amountNeeded ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl pl-11 pr-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors`} placeholder="e.g. 150000" required data-id="element-295" />
        </div>
        {validationErrors.amountNeeded && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-296">
            {validationErrors.amountNeeded}
          </p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-297">
        <div className="space-y-2" data-id="element-298">
          <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-299">
            Deal Type *
          </label>
          <select name="dealType" value={formData.dealType} onChange={handleChange} onFocus={() => setFocusedField('dealType')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.dealType ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl px-4 py-3.5 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors appearance-none`} required data-id="element-300">
            <option value="" disabled data-id="element-301">
              Select Type
            </option>
            <option value="Single Family" data-id="element-302">Single Family</option>
            <option value="Multi Family" data-id="element-303">Multi Family</option>
            <option value="Commercial" data-id="element-304">Commercial</option>
            <option value="Land" data-id="element-305">Land</option>
          </select>
          {validationErrors.dealType && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-306">
              {validationErrors.dealType}
            </p>}
        </div>

        <div className="space-y-2" data-id="element-307">
          <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-308">
            Inspection Period Ends *
          </label>
          <div className="relative" data-id="element-309">
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none" data-id="element-310">
              <CalendarIcon className={`w-5 h-5 transition-colors ${focusedField === 'inspectionEndDate' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-311" />
            </div>
            <DatePicker selected={inspectionDate} onChange={handleInspectionDateChange} onFocus={() => setFocusedField('inspectionEndDate')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.inspectionEndDate ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl pl-11 pr-4 py-3.5 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors`} placeholderText="Select Date" dateFormat="MM/dd/yyyy" minDate={new Date()} wrapperClassName="w-full" required data-id="element-312" />
          </div>
          {validationErrors.inspectionEndDate && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-313">
              {validationErrors.inspectionEndDate}
            </p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-314">
        <div className="space-y-2" data-id="element-315">
          <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-316">
            Exit Strategy *
          </label>
          <select name="exitStrategy" value={formData.exitStrategy} onChange={handleChange} onFocus={() => setFocusedField('exitStrategy')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.exitStrategy ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl px-4 py-3.5 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors appearance-none`} required data-id="element-317">
            <option value="" disabled data-id="element-318">
              Select Strategy
            </option>
            <option value="wholesale" data-id="element-319">Wholesale / Assignment</option>
            <option value="fixAndFlip" data-id="element-320">Fix & Flip</option>
            <option value="buyAndHold" data-id="element-321">Buy & Hold</option>
            <option value="other" data-id="element-322">Other</option>
          </select>
          {validationErrors.exitStrategy && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-323">
              {validationErrors.exitStrategy}
            </p>}
        </div>

        <div className="space-y-2" data-id="element-324">
          <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-325">
            Experience Level *
          </label>
          <select name="experienceLevel" value={formData.experienceLevel} onChange={handleChange} onFocus={() => setFocusedField('experienceLevel')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.experienceLevel ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl px-4 py-3.5 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors appearance-none`} required data-id="element-326">
            <option value="" disabled data-id="element-327">
              Select Experience
            </option>
            <option value="beginner" data-id="element-328">Beginner (0–2 deals)</option>
            <option value="intermediate" data-id="element-329">Intermediate (3–10 deals)</option>
            <option value="experienced" data-id="element-330">Experienced (10+ deals)</option>
          </select>
          {validationErrors.experienceLevel && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-331">
              {validationErrors.experienceLevel}
            </p>}
        </div>
      </div>

      <div className="space-y-2" data-id="element-332">
        <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-333">
          Closing Date *
        </label>
        <div className="relative" data-id="element-334">
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none" data-id="element-335">
            <CalendarIcon className={`w-5 h-5 transition-colors ${focusedField === 'closingDate' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-336" />
          </div>
          <DatePicker selected={closingDateValue} onChange={handleClosingDateChange} onFocus={() => setFocusedField('closingDate')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.closingDate ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl pl-11 pr-4 py-3.5 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors`} placeholderText="Select Closing Date" dateFormat="MM/dd/yyyy" minDate={new Date()} wrapperClassName="w-full" required data-id="element-337" />
        </div>
        {validationErrors.closingDate && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-338">
            {validationErrors.closingDate}
          </p>}
      </div>

      <div className="space-y-2" data-id="element-339">
        <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-340">
          Brief Deal Explanation *
        </label>
        <textarea name="dealExplanation" value={formData.dealExplanation} onChange={handleChange} onFocus={() => setFocusedField('dealExplanation')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#1c1916] border ${validationErrors.dealExplanation ? 'border-red-500' : 'border-[#403a35]'} rounded-2xl px-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors min-h-[100px] resize-y`} placeholder="Tell us briefly about the deal, purchase price, ARV, etc." required data-id="element-341" />
        {validationErrors.dealExplanation && <p className="text-red-500 text-xs mt-1 ml-1" data-id="element-342">
            {validationErrors.dealExplanation}
          </p>}
      </div>
    </div>;
  const renderStep4 = () => <div className="space-y-5 animate-fadeIn" data-id="element-343">
      <h3 className="text-2xl font-bold text-[#f2efeb] mb-2 flex items-center gap-2" data-id="element-344">
        <FileTextIcon className="w-6 h-6 text-[#c9a875]" data-id="element-345" />
        Contracts (Optional)
      </h3>
      <p className="text-[#f2efeb]/60 text-sm mb-6" data-id="element-346">
        Provide links to your contracts if you have them ready. You can use
        Google Drive, Dropbox, DocuSign, etc. This helps speed up the process
        but is not required to submit.
      </p>

      <div className="bg-[#1c1916] border border-[#403a35] rounded-2xl p-5 mb-6" data-id="element-347">
        <div className="flex items-start gap-3" data-id="element-348">
          <AlertCircleIcon className="w-5 h-5 text-[#c9a875] flex-shrink-0 mt-0.5" data-id="element-349" />
          <p className="text-sm text-[#f2efeb]/80" data-id="element-350">
            <strong data-id="element-351">Note:</strong> Please ensure your contract links are set to
            "Anyone with the link can view" so our team can access them.
          </p>
        </div>
      </div>

      <div className="space-y-2" data-id="element-352">
        <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-353">
          Purchase Contract URL (Optional)
        </label>
        <div className="relative" data-id="element-354">
          <FileTextIcon className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'purchaseContractUrl' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-355" />
          <input type="url" name="purchaseContractUrl" value={formData.purchaseContractUrl} onChange={handleChange} onFocus={() => setFocusedField('purchaseContractUrl')} onBlur={() => setFocusedField(null)} className="w-full bg-[#1c1916] border border-[#403a35] rounded-2xl pl-11 pr-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors" placeholder="https://drive.google.com/..." data-id="element-356" />
        </div>
      </div>

      <div className="space-y-2" data-id="element-357">
        <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-358">
          A-B Contract URL (Optional)
        </label>
        <div className="relative" data-id="element-359">
          <FileTextIcon className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'abContractUrl' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-360" />
          <input type="url" name="abContractUrl" value={formData.abContractUrl} onChange={handleChange} onFocus={() => setFocusedField('abContractUrl')} onBlur={() => setFocusedField(null)} className="w-full bg-[#1c1916] border border-[#403a35] rounded-2xl pl-11 pr-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors" placeholder="https://drive.google.com/..." data-id="element-361" />
        </div>
      </div>

      <div className="space-y-2" data-id="element-362">
        <label className="text-sm font-medium text-[#f2efeb]/80 ml-1" data-id="element-363">
          B-C Contract URL (Optional)
        </label>
        <div className="relative" data-id="element-364">
          <FileTextIcon className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'bcContractUrl' ? 'text-[#c9a875]' : 'text-[#f2efeb]/40'}`} data-id="element-365" />
          <input type="url" name="bcContractUrl" value={formData.bcContractUrl} onChange={handleChange} onFocus={() => setFocusedField('bcContractUrl')} onBlur={() => setFocusedField(null)} className="w-full bg-[#1c1916] border border-[#403a35] rounded-2xl pl-11 pr-4 py-3 text-[#f2efeb] focus:outline-none focus:border-[#c9a875] transition-colors" placeholder="https://drive.google.com/..." data-id="element-366" />
        </div>
      </div>
    </div>;
  const renderStep5 = () => <div className="space-y-5 animate-fadeIn" data-id="element-367">
      <h3 className="text-2xl font-bold text-[#f2efeb] mb-6 flex items-center gap-2" data-id="element-368">
        <CheckCircleIcon className="w-6 h-6 text-[#c9a875]" data-id="element-369" />
        Review & Submit
      </h3>

      <div className="bg-[#1c1916] rounded-2xl p-6 border border-[#403a35] mb-8 max-w-md mx-auto text-left" data-id="element-370">
        <h4 className="font-bold text-[#f2efeb] mb-4 border-b border-[#403a35] pb-2" data-id="element-371">
          Summary
        </h4>
        <div className="space-y-3 text-sm" data-id="element-372">
          <div className="flex justify-between" data-id="element-373">
            <span className="text-[#f2efeb]/60" data-id="element-374">Name:</span>
            <span className="text-[#f2efeb] font-medium" data-id="element-375">
              {formData.firstName} {formData.lastName}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-376">
            <span className="text-[#f2efeb]/60" data-id="element-377">Property:</span>
            <span className="text-[#f2efeb] font-medium" data-id="element-378">
              {formData.city}, {formData.state}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-379">
            <span className="text-[#f2efeb]/60" data-id="element-380">Amount Needed:</span>
            <span className="text-[#c9a875] font-bold" data-id="element-381">
              ${formData.amountNeeded}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-382">
            <span className="text-[#f2efeb]/60" data-id="element-383">Closing Date:</span>
            <span className="text-[#f2efeb] font-medium" data-id="element-384">
              {formData.closingDate}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-385">
            <span className="text-[#f2efeb]/60" data-id="element-386">Contracts:</span>
            <span className="text-[#f2efeb] font-medium" data-id="element-387">
              {formData.purchaseContractUrl || formData.abContractUrl || formData.bcContractUrl ? 'Provided' : 'Not Provided'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3 mt-6" data-id="element-388">
        <input type="checkbox" id="termsAgreed" name="termsAgreed" checked={formData.termsAgreed} onChange={handleChange} className="h-5 w-5 text-[#c9a875] rounded border-[#403a35] bg-[#1c1916] focus:ring-[#c9a875] mt-0.5" data-id="element-389" />
        <label htmlFor="termsAgreed" className="text-sm text-[#f2efeb]/80 leading-tight cursor-pointer" data-id="element-390">
          I confirm that the information provided is accurate and I agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#c9a875] hover:underline" data-id="element-391">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#c9a875] hover:underline" data-id="element-392">
            Privacy Policy
          </a>
          .
        </label>
      </div>
      {validationErrors.termsAgreed && <p className="text-red-500 text-xs mt-1 ml-8" data-id="element-393">
          {validationErrors.termsAgreed}
        </p>}
    </div>;
  return <div className="bg-[#2b2826] rounded-3xl p-8 sm:p-10 md:p-12 border border-[#403a35] shadow-2xl relative w-full max-w-[550px]" data-id="element-394">
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#c9a875]/20 via-[#c9a875] to-[#c9a875]/20" data-id="element-395"></div>

      <div className="text-center mb-8" data-id="element-396">
        <h2 className="text-3xl font-bold text-[#f2efeb] mb-2 font-serif-display" data-id="element-397">
          Get Funded Today
        </h2>
        <p className="text-[#f2efeb]/60" data-id="element-398">
          Complete this quick application to get started.
        </p>
      </div>

      {submissionError && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-2xl flex items-start gap-3" data-id="element-399">
          <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-400" />
          <p className="text-sm text-red-500" data-id="element-401">{submissionError}</p>
        </div>}

      {renderStepIndicator()}

      <form onSubmit={handleSubmit} className="relative" data-id="element-402">
        <div className="min-h-[320px]" data-id="element-403">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
        </div>

        <div className="mt-10 flex gap-4 pt-6 border-t border-[#403a35]" data-id="element-404">
          {currentStep > 1 && <button type="button" onClick={prevStep} className="flex items-center justify-center text-[#f2efeb]/70 font-medium py-3 px-6 rounded-full hover:text-[#f2efeb] hover:bg-[#1c1916] transition-all" data-id="element-405">
              <ArrowLeftIcon className="w-5 h-5 mr-2" data-id="element-406" />
              Back
            </button>}

          {currentStep < totalSteps ? <button type="button" onClick={nextStep} className="flex-1 bg-[#c9a875] text-white font-medium py-4 px-6 rounded-full hover:bg-[#b08e5c] transition-all shadow-lg hover:shadow-[#c9a875]/25 flex items-center justify-center group" data-id="element-407">
              Continue
              <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-408" />
            </button> : <button type="submit" disabled={isSubmitting} className={`flex-1 bg-[#c9a875] text-white font-medium py-4 px-6 rounded-full hover:bg-[#b08e5c] transition-all shadow-lg hover:shadow-[#c9a875]/25 flex items-center justify-center group ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-409">
              {isSubmitting ? <span className="flex items-center" data-id="element-410">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-411">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-412"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-413"></path>
                  </svg>
                  Processing...
                </span> : <span className="flex items-center" data-id="element-414">
                  Submit Application
                  <CheckIcon className="w-5 h-5 ml-2" data-id="element-415" />
                </span>}
            </button>}
        </div>
      </form>
    </div>;
};